import { graphql } from 'react-relay';
import type { ComponentProps } from 'react';
import React, { useContext } from 'react';
// We have deprecated unstated. Please use react-sweet-state instead
// eslint-disable-next-line no-restricted-imports
import { Subscribe } from 'unstated';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { UnarchivePagesDialogLoader } from '@confluence/archive-pages/entry-points/UnarchivePagesLoader.ts';
import { ArchiveUpsellDialog } from '@confluence/archive-pages/entry-points/ArchiveUpsellDialog.ts';
import { ArchivePagesLoader } from '@confluence/archive-pages/entry-points/ArchivePagesLoader.ts';
import { canUnarchivePage } from '@confluence/archive-pages/entry-points/PageOperations.ts';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';
import { DialogsStateContainer } from '@confluence/dialogs/entry-points/DialogsStateContainer.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';
import {
	ARCHIVE_PAGES_EXPERIENCE,
	RESTORE_PAGES_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import type { FormattedWebItem } from '@confluence/web-item-location/entry-points/WebItemLocation.ts';
import type {
	ArchivePageItemShowArchiveCTAQuery$data,
	ArchivePageItemShowArchiveCTAQuery$variables,
} from './__generated__/ArchivePageItemShowArchiveCTAQuery.graphql.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import { LazyContentToolsItem } from './LazyContentToolsItem.tsx';
export const ACTION_ARCHIVE_PAGE_LINK = 'action-archive-link';
export const ACTION_RESTORE_PAGE_LINK = 'action-restore-link';

export const ARCHIVE_BUTTON_ANALYTICS_SOURCE = 'contentToolsItem';

export const ArchivePageItem: React.FC<ArchivePageItemComponentProps> = withErrorBoundary({
	attribution: Attribution.ADMIN_EXPERIENCE,
})(ArchivePageItemComponent);

function ArchivePageItemComponent({
	contentId,
	sectionItem,
	...props
}: ArchivePageItemComponentProps) {
	const { edition } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { data, error, loading } = useQueryInterop<
		ArchivePageItemShowArchiveCTAQuery$data,
		ArchivePageItemShowArchiveCTAQuery$variables
	>(
		graphql`
			query ArchivePageItemShowArchiveCTAQuery($contentId: ID!) {
				content(id: $contentId) {
					nodes {
						id
						operations {
							operation
							targetType
						}
					}
				}
			}
		`,
		{
			variables: { contentId },
		},
	);
	if (loading) {
		return null;
	}
	if (error) {
		return <ErrorDisplay error={error} />;
	}
	const isFreeEdition = edition === ConfluenceEdition.FREE;
	const isArchiveAction = sectionItem.id === ACTION_ARCHIVE_PAGE_LINK;
	// If user is on Free edition and the current sectionItem.id is Archive,
	// display the Archive button.
	// If user is on Premium or Standard, check if the user has permissions to
	// archive/restore the page before displaying the Archive or Restore button.
	if (
		(isFreeEdition && isArchiveAction) ||
		canUnarchivePage(data?.content?.nodes?.[0]?.operations)
	) {
		return (
			<Subscribe to={[DialogsStateContainer]}>
				{(dialogs: DialogsStateContainer) => {
					const onClick = () => {
						createAnalyticsEvent({
							type: 'sendUIEvent',
							data: {
								action: 'clicked',
								actionSubject: isArchiveAction ? 'archiveButton' : 'restoreButton',
								actionSubjectId: isFreeEdition ? 'archivePageUpsell' : null,
								source: ARCHIVE_BUTTON_ANALYTICS_SOURCE,
								attributes: {
									confluenceEdition: edition,
								},
							},
						}).fire();
						if (isFreeEdition) {
							dialogs.showDialog(ArchiveUpsellDialog);
						} else {
							// Don't need to track experience for free edition as there is no archiving experience in free
							experienceTracker.start({
								name: isArchiveAction ? ARCHIVE_PAGES_EXPERIENCE : RESTORE_PAGES_EXPERIENCE,
							});
							dialogs.showDialog(
								// @ts-expect-error ArchivePagesLoader & UnarchivePagesDialogLoader predate the TypeScript support here
								isArchiveAction ? ArchivePagesLoader : UnarchivePagesDialogLoader,
								{ contentId, source: ARCHIVE_BUTTON_ANALYTICS_SOURCE },
							);
						}
					};
					return (
						<LazyContentToolsItem
							{...sectionItem}
							{...props}
							label={sectionItem.label}
							url={undefined}
							onClick={onClick}
						/>
					);
				}}
			</Subscribe>
		);
	}
	return null;
}
type ArchivePageItemComponentProps = {
	contentId: string;
	sectionItem: FormattedWebItem;
} & Partial<ComponentProps<typeof LazyContentToolsItem>>;
